<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { resolveImageAlt } from "@/utils/imageAlt";

const pageRef = usePage();
const page = computed(() => pageRef.value);
const { gsap, loadGSAP } = useGSAP();
const img = useImage();

onMounted(() => {
  loadGSAP();
});

const minHeight = ref("min-height: 100svh;");

function updateWidth() {
  if (typeof window === "undefined") return;
  const mobile = window.innerWidth <= 991;
  minHeight.value = mobile ? "min-height: 100svh;" : "min-height: 100vh;";
}

onMounted(() => {
  updateWidth();
  window.addEventListener("resize", updateWidth);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateWidth);
});

const heroAlt = computed(() =>
  resolveImageAlt(
    page.value?.heroImage?.alt,
    page.value?.heroImageTitle || page.value?.title,
  ),
);

/** Art-direction srcsets so SSR HTML includes the correct mobile crop. */
const mobileSrcset = computed(() => {
  const src = page.value?.heroImage?.urlMobile || page.value?.heroImage?.url;
  if (!src) return "";
  return img(src, { width: 900, format: "webp", quality: 75 });
});

const desktopSrcset = computed(() => {
  const src = page.value?.heroImage?.url;
  if (!src) return "";
  return img(src, { width: 1780, format: "webp", quality: 75 });
});

const fallbackSrc = computed(
  () => page.value?.heroImage?.urlMobile || page.value?.heroImage?.url || "",
);

const scrollTo = () => {
  if (gsap.value) {
    gsap.value.to(window, {
      duration: 1,
      scrollTo: {
        y: "#trekPackages",
        offsetY: 300,
      },
      ease: "power2.inOut",
    });
  }
};
const scrollToIntro = () => {
  if (gsap.value) {
    gsap.value.to(window, {
      duration: 1,
      scrollTo: {
        y: "#scrollToIntro",
        offsetY: 100,
      },
      ease: "power2.inOut",
    });
  }
};

// Preload the LCP image for both breakpoints (browser picks via media)
useHead({
  link: computed(() => {
    const links: Array<Record<string, string>> = [];
    if (mobileSrcset.value) {
      links.push({
        rel: "preload",
        as: "image",
        href: mobileSrcset.value,
        media: "(max-width: 991px)",
        fetchpriority: "high",
      });
    }
    if (desktopSrcset.value) {
      links.push({
        rel: "preload",
        as: "image",
        href: desktopSrcset.value,
        media: "(min-width: 992px)",
        fetchpriority: "high",
      });
    }
    return links;
  }),
});
</script>

<template>
  <div
    v-if="page?.heroImage"
    class="main-hero hero"
    :class="{
      'hero-height-small':
        page?.template !== 'home' && page?.template !== 'treks',
    }"
  >
    <div class="main-hero-image">
      <AppSVGBrandGraphic />
      <div
        v-if="page?.template === 'treks'"
        class="chevron-container"
        @click.prevent="scrollToIntro"
      >
        <svg class="chevron" width="40" height="40" viewBox="0 0 24 24">
          <path
            d="M12 16l-6-6 1.41-1.41L12 13.17l4.59-4.59L18 10l-6 6z"
            fill="white"
          ></path>
        </svg>
        <svg class="chevron" width="40" height="40" viewBox="0 0 24 24">
          <path
            d="M12 16l-6-6 1.41-1.41L12 13.17l4.59-4.59L18 10l-6 6z"
            fill="white"
          ></path>
        </svg>
      </div>
      <AppHeroCta />
      <div class="page-title-wrapper">
        <div v-if="page?.heroImageTitle" class="page-title">
          <h2>{{ page?.heroImageTitle }}</h2>
          <h3>{{ page?.heroImageSubtitle }}</h3>
          <div
            v-if="page?.template === 'treks'"
            class="hero-cta-btn btn from-left"
            @click.prevent="scrollTo"
          >
            View Trek Packages
          </div>
        </div>
        <div v-else class="page-title">
          <h2>{{ page?.title }}</h2>
        </div>
      </div>
      <div class="hero-fade"></div>
      <div :class="page?.heroImagePosition">
        <picture class="hero-image">
          <source
            v-if="mobileSrcset"
            media="(max-width: 991px)"
            :srcset="mobileSrcset"
            type="image/webp"
          />
          <source
            v-if="desktopSrcset"
            media="(min-width: 992px)"
            :srcset="desktopSrcset"
            type="image/webp"
          />
          <img
            :src="fallbackSrc"
            :width="page?.heroImage?.width || 1780"
            :height="page?.heroImage?.height || 1000"
            :alt="heroAlt"
            fetchpriority="high"
            decoding="async"
          />
        </picture>
      </div>
    </div>
    <AppAnnouncement />
    <div v-if="page?.template === 'treks'" id="scrollToIntro"></div>
  </div>
</template>
